import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useSelector, useDispatch } from 'react-redux'
import DropdownMenu from 'GeneralUI/DropdownMenu/DropdownMenu.jsx'
import { verify2Fa } from 'Helper/Auth/summit-auth-api.js'
import { attemptLogin } from 'actions/authActions'
import Waiting from '../Waiting.jsx'
import { useNavigate } from 'react-router-dom'
import { addToClickLog } from 'actions/logActions'


const LoginPanel = ({ setStateMessageRef, message, retryAccessTokenGrant, setSummitLoginRequired }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const emailEnabled = useSelector(state => state.appConfig.email_enabled)
  const [password, setPassword] = useState('')
  const [panelState, setPanelState] = useState('login')
  const [twoFactorCode, setTwoFactorCode] = useState('')
  const [doRecovery, setDoRecovery] = useState(false)
  const setPanelStateRef = useRef(0)
  const [errors, setErrors] = useState([])
  const [domain, setDomain] = useState(
    localStorage.getItem('default_ad_domain') || ''
  )
  const { ad_domains: adDomains, username: lastUsername, auth_provider: lastAuthProvider } = useSelector(state => state.auth)
  const [username, setUsername] = useState(lastAuthProvider === 'summit' && lastUsername ? lastUsername : '')

  useEffect(() => {
    // componentDidMount
    setPanelStateRef.current = setPanelState
    return () => {
      setPanelStateRef.current = () => null
    }
  }, [])

  const loginForbiddenCallback = useCallback(response => {
    dispatch(addToClickLog({ event: "Login: Login Forbidden" }))
    const { locked, password_expired: passwordExpired, missing_email: missingEmail, unverified_email: unverifiedEmail } = response.data.message
    // note... this section is only encountered when using summit auth however missing and unverified email can also
    // occurre with non-summit auth so it is handeled by a route forwared where as password expiration only occurs
    // when using summit auth so it is handled by a state/message change.
    if (locked) {
      setStateMessageRef.current('error', 'This account has been locked. Please contact the system administrator.')
    } else if (passwordExpired) {
      setStateMessageRef.current('recover-password', 'Your password has expired. Please click the recovery link in your email or use this form to send a new recovery email.')
    } else if (missingEmail) {
      navigate('/login?missing_email=true')
    } else if (unverifiedEmail) {
      navigate('/login?unverified_email=true')
    } else {
      setStateMessageRef.current('error', 'Login forbidden. Please contact your system administrator for assistance')
    }
  }, [])

  const loginSuccessCallback = useCallback((response) => {
    dispatch(addToClickLog({ event: "Login: Login Successful" }))
    const { two_factor: twoFactor } = response.data.data.login
    if (twoFactor) {
      setStateMessageRef.current('summit-login', 'Please enter your ' + (doRecovery ? 'recovery code.' : 'two factor verification token.'))
      setPanelState('2-factor')
    } else {
      setSummitLoginRequired(false)
    }
  }, [])

  const loginFailedCallback = useCallback((response) => {
    dispatch(addToClickLog({ event: "Login: Login Failed" }))
    setPanelStateRef.current('login')
    if (response.status === 419) {
      setErrors({ CSRF: ['CSRF token mismatch. Please reload the page and try again.'] })
    } else {
      const data = response?.data
      const errors = data?.errors
      if (errors && Object.keys(errors).length > 0) {
        setErrors(errors)
      } else {
        const message = typeof data?.message === 'string' ? data.message : 'An unknown error occurred'
        setErrors({ error: [message] })
      }
    }
  }, [])

  const doLogin = useCallback(e => {
    e.preventDefault()
    if (username !== '') {
      // dont bother submitting the login attempt if they didn't give a username
      setPassword('')
      setPanelState('login-in-progress')
      dispatch(attemptLogin(username, password, {
        200: (action, response) => loginSuccessCallback(action, response),
        302: (action, response) => loginSuccessCallback(action, response),
        403: (action, response) => loginForbiddenCallback(action, response),
        validation: (action, response) => loginFailedCallback(action, response),
        error: (action, response) => loginFailedCallback(action, response)
      }))
    }
  }, [username, password])

  const activeDirectoryControls = useMemo(() => {
    if (!adDomains) {
      return null
    } else {
      const options = adDomains.map(domain => {
        return { name: domain, value: domain }
      })
      return (
        <DropdownMenu
          className="domain"
          onChange={e => {
            localStorage.setItem('default_ad_domain', e)
            setDomain(e)
          }}
          value={domain}
          options={options}
        />
      )
    }
  }, [adDomains])

  const Message = useMemo(() => {
    if (!message) {
      return null
    } else {
      return (
        <div className="login-text" style={{ marginTop: '20px' }}>
        {message}
        {message !== null ? <p /> : null}
      </div>
      )
    }
  }, [message])

  const ForgotPasswordLink = useMemo(() => {
    if (emailEnabled) {
      return (
        <div
          onClick={() => {
            // setting panel state of SummitAuth component
            setStateMessageRef.current('recover-password', null)
            setPassword('')
          }}
          aria-label="forgot password"
          className="login-text forgot-password btn"
        >
          Forgot password?
        </div>
      )
    } else {
      return null
    }
  }, [emailEnabled])

  const do2FactorVerify = useCallback((e) => {
    e.preventDefault()
    setPanelState('2-factor-inprogress')
    const standard = doRecovery ? null : twoFactorCode
    const recovery = doRecovery ? twoFactorCode : null
    verify2Fa(standard, recovery).then(result => {
      if (result.status === 204 || result.status === 200) {
        retryAccessTokenGrant()
      } else {
        if (result.status === 419) {
          setStateMessageRef.current('summit-login', 'Page expired. Please reload page and try again.')
          setPanelState('login')
        } else if (result.status === 422) {
          setStateMessageRef.current('summit-login', 'Invalid token. Please try again.')
          setPanelState('2-factor')
        } else {
          setStateMessageRef.current('summit-login', 'An unknown error ocurred.')
          setPanelState('login')
        }
      }
    })
  }, [twoFactorCode, doRecovery, setStateMessageRef])

  if (panelState === 'login-in-progress') {
    return (
      <Waiting message="Attempting authentication..." />
    )
  } else if (panelState === '2-factor-inprogress') {
    return (
      <Waiting message="Verifying token..." />
    )
  } else if (panelState === '2-factor') {
    return (
      <React.Fragment>
        {Message}
        <form className="login-inputs" onSubmit={do2FactorVerify}>
          <input
            value={twoFactorCode}
            onChange={e => setTwoFactorCode(e.target.value)}
            placeholder={doRecovery ? 'Recovery code' : 'Two factor token'}
            aria-label="2 factor code input"
            className="form-control input-lg"
            type="text"
          />
          <input className="hidden" type="submit" />
        </form>
        <br />
        <button
          type="submit"
          aria-label="submit button"
          className="btn btn-success btn-lg login-button"
          onClick={do2FactorVerify}
        >
          Submit
        </button>
        <div
          onClick={() => {
            setDoRecovery(!doRecovery)
          }}
          aria-label="use two factor recovery code?"
          className="login-text forgot-password btn"
        >
          {doRecovery ? 'Use token' : 'Use recovery code'}
        </div>
      </React.Fragment>
    )
  } else {
    return (
      <React.Fragment>
        <h1>Sign In</h1>
        {Message}
        { errors && Object.keys(errors).length > 0
          ? <div
              className="alert alert-danger animate__animated animate__pulse"
              role="alert"
              data-cy="alert"
            >
              <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} size="1x" />
              <div style={{ display: 'inline' }} >
                {Object.keys(errors).map((type, idx) => {
                  return (
                    <React.Fragment key={type}>
                      {`${type}:`}
                      <ul key={idx}>
                        {errors[type].map((subError, idx2) => <li key={idx2}>{subError}</li>)}
                      </ul>
                    </React.Fragment>
                  )
                })}
              </div>
            </div>
          : null
        }
        {adDomains == null ? null : activeDirectoryControls}
        <form className="login-inputs" onSubmit={doLogin}>
          <input
            value={username}
            onChange={e => setUsername(e.target.value)}
            placeholder="Username"
            aria-label="Login: Enter Username"
            className="form-control input-lg"
            type="text"
            autoFocus
            data-cy="username"
          />
          <br />
          <input
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Password"
            aria-label="Login: Enter Password"
            className="form-control input-lg"
            type="password"
            data-cy="password"
          />
          <input className="hidden" type="submit" />
        </form>
        <br />
        <button
          type="submit"
          aria-label="Login: Submit Button"
          className="btn btn-success btn-lg login-button"
          onClick={doLogin}
        >
          Submit
        </button>
        {ForgotPasswordLink}
      </React.Fragment>
    )
  }
}

export default LoginPanel
